import type { GeneralInfoPicklistItem } from '../api/generalInfoApi';
import { ISRAEL_COUNTRY_ID } from './assetCountryCurrency';
import { assetCreateShowsRegionField } from './assetCreateFormDefaults';
import { localizeAssetFieldLabel } from '../i18n';
import type { TranslateFn } from '../i18n/operationLabels';
import { buildAddressGeocodeQuery, isDefaultJerusalemCoordinates, parseAssetCoordinates } from './assetGeo';

export const ASSET_CREATE_MANDATORY_ROOT_KEYS = [
  'asset_name',
  'asset_type_id',
  'country_id',
] as const;

export const ASSET_CREATE_MANDATORY_ADDRESS_KEYS = [
  'city',
  'street',
  'building',
] as const;

/** Dotted paths for {@link DynamicRecordForm} mandatory markers (excludes US-only region). */
export const ASSET_CREATE_MANDATORY_MARKER_KEYS_BASE = [
  ...ASSET_CREATE_MANDATORY_ROOT_KEYS,
  ...ASSET_CREATE_MANDATORY_ADDRESS_KEYS.map((key) => `address.${key}`),
] as const;

/** @deprecated Use {@link assetCreateMandatoryMarkerKeys} — includes `state_id` only for the US. */
export const ASSET_CREATE_MANDATORY_MARKER_KEYS = [
  ...ASSET_CREATE_MANDATORY_MARKER_KEYS_BASE,
  'state_id',
] as const;

export function assetCreateMandatoryMarkerKeys(
  countryId: unknown,
): readonly string[] {
  if (assetCreateShowsRegionField(countryId)) {
    return [...ASSET_CREATE_MANDATORY_MARKER_KEYS_BASE, 'state_id'];
  }
  return [...ASSET_CREATE_MANDATORY_MARKER_KEYS_BASE];
}

export type AssetCreateValidationFailure = {
  fieldKey: string;
  addressField?: boolean;
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isEmptyRegisterValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return !value.trim();
  }
  if (typeof value === 'number') {
    return !Number.isFinite(value);
  }
  return false;
}

function coerceNumericId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export type ValidateAssetCreateBodyOptions = {
  regions: readonly GeneralInfoPicklistItem[];
  /** When false, region is not required even if the API returned regions. */
  requireRegionWhenListed?: boolean;
};

/**
 * Returns the first missing required create/update field, or null when the body can submit.
 */
export function missingAssetCreateRequiredField(
  body: Record<string, unknown>,
  options: ValidateAssetCreateBodyOptions,
): AssetCreateValidationFailure | null {
  for (const key of ASSET_CREATE_MANDATORY_ROOT_KEYS) {
    if (isEmptyRegisterValue(body[key])) {
      return { fieldKey: key };
    }
  }

  const countryId = coerceNumericId(body.country_id);
  const requireRegion =
    options.requireRegionWhenListed !== false &&
    countryId !== null &&
    countryId !== ISRAEL_COUNTRY_ID &&
    assetCreateShowsRegionField(countryId) &&
    options.regions.length > 0;
  if (requireRegion && isEmptyRegisterValue(body.state_id)) {
    return { fieldKey: 'state_id' };
  }

  const addr = body.address;
  if (!isPlainObject(addr)) {
    return { fieldKey: 'city', addressField: true };
  }
  for (const key of ASSET_CREATE_MANDATORY_ADDRESS_KEYS) {
    if (isEmptyRegisterValue(addr[key])) {
      return { fieldKey: key, addressField: true };
    }
  }

  const coords = parseAssetCoordinates(body);
  if (!coords) {
    return { fieldKey: 'latitude' };
  }

  if (buildAddressGeocodeQuery(body) && isDefaultJerusalemCoordinates(coords)) {
    return { fieldKey: 'latitude' };
  }

  return null;
}

export function assetCreateValidationErrorMessage(
  failure: AssetCreateValidationFailure,
  t: TranslateFn,
): string {
  if (failure.fieldKey === 'latitude') {
    return t('assets.geo.needAddress');
  }
  const fieldLabel = failure.addressField
    ? `${localizeAssetFieldLabel('address', t)} — ${localizeAssetFieldLabel(failure.fieldKey, t)}`
    : localizeAssetFieldLabel(failure.fieldKey, t);
  return t('register.error.fieldRequired', { field: fieldLabel });
}

/** Dotted paths for update form — name, type; US assets also require region when listed. */
export const ASSET_UPDATE_MANDATORY_MARKER_KEYS_BASE = ['asset_name', 'asset_type_id'] as const;

/** @deprecated Use {@link assetUpdateMandatoryMarkerKeys}. */
export const ASSET_UPDATE_MANDATORY_MARKER_KEYS = [
  ...ASSET_UPDATE_MANDATORY_MARKER_KEYS_BASE,
  'state_id',
] as const;

export function assetUpdateMandatoryMarkerKeys(
  countryId: unknown,
): readonly string[] {
  if (assetCreateShowsRegionField(countryId)) {
    return [...ASSET_UPDATE_MANDATORY_MARKER_KEYS_BASE, 'state_id'];
  }
  return [...ASSET_UPDATE_MANDATORY_MARKER_KEYS_BASE];
}

export type ValidateAssetUpdateBodyOptions = {
  regions: readonly GeneralInfoPicklistItem[];
};

/**
 * Update PATCH — only core identity fields are required; address and geo may stay as loaded.
 */
export function missingAssetUpdateRequiredField(
  body: Record<string, unknown>,
  options: ValidateAssetUpdateBodyOptions,
): AssetCreateValidationFailure | null {
  for (const key of ['asset_name', 'asset_type_id'] as const) {
    if (isEmptyRegisterValue(body[key])) {
      return { fieldKey: key };
    }
  }

  const countryId = coerceNumericId(body.country_id);
  const requireRegion =
    countryId !== null &&
    assetCreateShowsRegionField(countryId) &&
    options.regions.length > 0;
  if (requireRegion && isEmptyRegisterValue(body.state_id)) {
    return { fieldKey: 'state_id' };
  }

  return null;
}

export function assertAssetUpdateBodyReady(
  body: Record<string, unknown>,
  regions: readonly GeneralInfoPicklistItem[],
  t: TranslateFn,
): void {
  const missing = missingAssetUpdateRequiredField(body, { regions });
  if (missing) {
    throw new Error(assetCreateValidationErrorMessage(missing, t));
  }
}

export function assertAssetCreateBodyReady(
  body: Record<string, unknown>,
  regions: readonly GeneralInfoPicklistItem[],
  t: TranslateFn,
): void {
  const missing = missingAssetCreateRequiredField(body, { regions });
  if (missing) {
    throw new Error(assetCreateValidationErrorMessage(missing, t));
  }
}

/** True when create/update must block: no pricing rows, or any selected row lacks a valid price. */
export function assetBodyHasInvalidPricingPrices(body: Record<string, unknown>): boolean {
  const methods = body.pricing_methods;
  if (!Array.isArray(methods) || methods.length === 0) {
    return true;
  }
  for (const item of methods) {
    if (!isPlainObject(item)) {
      continue;
    }
    const price = item.price;
    if (price === null || price === undefined || price === '') {
      return true;
    }
    const n = typeof price === 'number' ? price : Number(String(price).trim());
    if (!Number.isFinite(n) || n < 0) {
      return true;
    }
  }
  return false;
}
